import { Box, Button, Divider, HStack, Image, Input, Modal, Pressable, ScrollView, Switch, Text, VStack, useColorModeValue, useToast } from 'native-base';
import * as React from 'react';
import { launchImageLibrary } from 'react-native-image-picker';
import { Auth as Authorization } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import * as AppActions from '../../redux/actions/appAction';
import * as SettingsActions from '../../redux/actions/settingsAction';
import * as UploadAction from '../../redux/actions/uploadAction';
import * as BluetoothActions from '../../redux/actions/bluetoothAction';
import * as UpdateActions from '../../redux/actions/updateAction';
import { IStore } from 'src/redux/reducers';
import { IS_TEST_ENV, S3_BUCKET_URL } from 'src/constants';
import { containerStyle } from 'src/styles/containerStyle';
import { textStyle } from 'src/styles/textStyle';
import { getText } from 'src/utils/i18n';
import { sendAccountDeletionEmail } from 'src/utils/emailUtils';
import { inputStyle } from 'src/styles/inputStyle';
import { isNative } from 'src/utils/utils';
import { isElectron } from 'src/utils/miscUtils';
import LanguageDropdown from '../../components/dropdown/LanguageDropdown';
import MaterialIconByName from 'src/components/MaterialIconByName';
import SeedContourSettingsSection from 'src/components/SeedContourSettingsSection';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';

export default function SettingsPage({ navigation }: { navigation: any }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const username = useSelector((store: IStore) => store.user.userProfile.response?.username);
    const orgId = useSelector((store: IStore) => store.user.userProfile.response?.org?.orgId);
    const updateLogoResponse = useSelector((store: IStore) => store.user.updateUserLogo.response);
    const userSettings = useSelector((store: IStore) => store.user.userProfile.response?.settings);
    const lightboxEnabled = isLightboxFeatureEnabled(userSettings);
    const colorMode = useSelector((store: IStore) => store.settings.colorMode);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const currentVersion = useSelector((store: IStore) => store.update.currentVersion);
    const effectiveOnline =
        isOnline !== false
        && (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' || navigator.onLine !== false);

    const [selectedImage, setSelectedImage] = React.useState<string | undefined>(undefined);
    const [isUploadLogsLoading, setIsUploadLogsLoading] = React.useState<boolean>(false);

    const [deletionReason, setDeletionReason] = React.useState<string>("");
    const [isDeleteAccountModalVisible, setIsDeleteAccountModalVisible] = React.useState<boolean>(false);
    const [isRequestingDeletion, setIsRequestingDeletion] = React.useState<boolean>(false);

    const rpiConnected = useSelector((store: IStore) => store.bluetooth.connectedRpi.connectedDevice);
    const showLocalProcessingSettings = isElectron();

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
    const iconColor = useColorModeValue('blue.600', 'blue.400');

    React.useEffect(() => {
        if (updateLogoResponse) {
            toast.show({
                title:getText('logoUpdated'),
                placement: 'bottom',
            })
        }
    }, [updateLogoResponse])

    React.useEffect(() => {
        if (!userSettings?.logo) {
            setSelectedImage(undefined);
            return;
        }
        setSelectedImage(`${S3_BUCKET_URL()}/${userSettings.logo}`);
    }, [userSettings])

    React.useEffect(() => {
        if (!isElectron() || typeof window === 'undefined') {
            return;
        }

        const electronAPI = (window as any).electronAPI;
        if (typeof electronAPI?.getAppVersion !== 'function') {
            return;
        }

        let cancelled = false;
        const releaseStage = typeof process !== 'undefined' && process.env?.RELEASE_STAGE
            ? process.env.RELEASE_STAGE
            : 'staging';

        electronAPI.getAppVersion(releaseStage)
            .then((version: string) => {
                if (!cancelled && version && version !== currentVersion) {
                    dispatch(UpdateActions.setCurrentVersion(version));
                }
            })
            .catch((error: any) => {
                console.warn('Failed to load Electron app version:', error?.message || error);
            });

        return () => {
            cancelled = true;
        };
    }, [currentVersion, dispatch]);

    const checkAuthAndUpload = async (data: any) => {
        if (!effectiveOnline) {
            toast.show({
                title: 'Offline',
                description: 'Connect to the internet to upload your logo.',
                placement: 'bottom',
            });
            return;
        }
        Authorization.currentUserInfo().then(async (user) => {
            setSelectedImage(data?.assets[0].uri)
            dispatch(UploadAction.uploadLogo.request({
                type: 'user',
                username: username,
                isBase64: false,
                orgId: orgId,
                dataObject: data?.assets[0]
            }));

        }).catch((e) => {
            console.log('force logout')
            if (effectiveOnline) {
                if (isElectron()) {
                    dispatch(AppActions.setCloudAuthStatus({
                        status: 'invalid',
                        reason: 'auth_check_failed',
                        updatedAt: Date.now(),
                    }));
                    toast.show({
                        title: 'Sign-in expired',
                        description: 'Open Desktop Settings to re-authenticate and resume sync.',
                        placement: 'bottom',
                    });
                } else {
                    dispatch(AppActions.forceLogout());
                }
            } else {
                toast.show({
                    title: 'Offline',
                    description: 'Connect to the internet to upload your logo.',
                    placement: 'bottom',
                });
            }
            console.log('error occurred', e)
        });
    }

    const onEditLogoClicked = () => {
        launchImageLibrary({
            selectionLimit: 0,
            mediaType: 'photo',
            includeBase64: false,
        }, checkAuthAndUpload)
    }

    const uploadIotLogs = async () => {
        setIsUploadLogsLoading(true)
        setTimeout(() => {
            setIsUploadLogsLoading(false)
        }, 3 * 1000)
        dispatch(BluetoothActions.uploadBleLogs.request({
            username: username,
            isTest: IS_TEST_ENV() || false
        }));
    }

    const canNavigateTo = React.useCallback(
        (routeName: string) => {
            const state = navigation?.getState?.();
            if (state?.routeNames?.includes(routeName)) return true;

            const parent = navigation?.getParent?.();
            const parentState = parent?.getState?.();
            if (parentState?.routeNames?.includes(routeName)) return true;

            return false;
        },
        [navigation]
    );

    return (
        <>
            <Modal
                size={"lg"}
                isOpen={isDeleteAccountModalVisible}
                onClose={() => {
                    setIsDeleteAccountModalVisible(false);
                    setDeletionReason("");
                }}>
                <Modal.Content>
                    <Modal.CloseButton />
                    <Modal.Header>{getText('deleteAccount')}</Modal.Header>
                    <Modal.Body>
                        <VStack p="2" space={4}>
                            <Text>{getText('deletionWarning')}
                            </Text>

                            <Input
                                key={"deletionReason"}
                                style={inputStyle.measurementInput}
                                placeholder={getText('deletionReason')}
                                onChangeText={(text) => {
                                    setDeletionReason(text)
                                }}
                                value={deletionReason} />
                        </VStack>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={2}>
                            <Button variant="ghost" colorScheme="blueGray" onPress={() => {
                                setIsDeleteAccountModalVisible(false)
                                setDeletionReason("")
                            }}>
                                {getText('cancel')}
                            </Button>
                            <Button
                                colorScheme="danger"
                                isLoading={isRequestingDeletion}
                                isLoadingText={getText('requestingDeletion')}
                                onPress={async () => {
                                    setIsRequestingDeletion(true);
                                    try {
                                        await sendAccountDeletionEmail(username, deletionReason);
                                        toast.show({
                                            title: getText('reqAccDeletion'),
                                            placement: 'bottom',
                                        });
                                    } catch (e) {
                                        toast.show({
                                            title: getText('errorFetchData'),
                                            placement: 'bottom',
                                        });
                                    } finally {
                                        setIsRequestingDeletion(false);
                                        setIsDeleteAccountModalVisible(false);
                                        setDeletionReason("");
                                    }
                                }
                                }>{getText('reqAccDeletion')}</Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
            <Box variant={"main"} style={containerStyle.mainContainer}>
                <ScrollView>
                    <VStack p={isElectron() ? 6 : 4} space={isElectron() ? 8 : 6} maxW={isElectron() ? "800px" : "100%"} alignSelf="center" w="100%">
                        {/* Organization */}
                        <VStack space={2}>
                            <Text style={textStyle.subSectionText}>Organization</Text>
                            <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} p={isElectron() ? 6 : 4} shadow={isElectron() ? "2" : "0"}>
                                <VStack space={3}>
                                    <Pressable onPress={onEditLogoClicked}>
                                        <Image
                                            style={{
                                                width: isElectron() ? 120 : 100,
                                                height: isElectron() ? 120 : 100,
                                                borderRadius: 8,
                                                alignSelf: "center",
                                            }}
                                            source={{
                                                uri: selectedImage || "https://cdn.pixabay.com/photo/2015/04/23/22/00/tree-736885__480.jpg",
                                            }}
                                            alt="Organization Logo"
                                            size="xl"
                                        />
                                    </Pressable>
                                    <Text fontSize="xs" color={secondaryTextColor} textAlign="center">
                                        Tap the logo to update it.
                                    </Text>
                                    <Button onPress={onEditLogoClicked}>{getText('updateLogo')}</Button>
                                </VStack>
                            </Box>
                        </VStack>

                        {/* Account */}
                        <VStack space={2}>
                            <Text style={textStyle.subSectionText}>{getText('account')}</Text>
                            <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} overflow="hidden" shadow={isElectron() ? "2" : "0"}>
                                <VStack divider={<Divider />} space={0}>
                                    {canNavigateTo('ManageUserSubscription') && (
                                        <Pressable
                                            onPress={() => navigation.navigate('ManageUserSubscription')}
                                            px={isElectron() ? 5 : 4}
                                            py={isElectron() ? 4 : 3}
                                        >
                                            <HStack space={3} alignItems="center">
                                                <MaterialIconByName name="credit-card-outline" size="md" color={iconColor} />
                                                <VStack flex={1} space={0.5}>
                                                    <Text fontSize="sm" fontWeight="600">
                                                        {getText('manageSubscription')}
                                                    </Text>
                                                    <Text fontSize="xs" color={secondaryTextColor}>
                                                        Billing and plan details
                                                    </Text>
                                                </VStack>
                                                <MaterialIconByName name="chevron-right" size="sm" color={secondaryTextColor} />
                                            </HStack>
                                        </Pressable>
                                    )}
                                    <Pressable onPress={() => navigation.navigate('ChangePassword')} px={isElectron() ? 5 : 4} py={isElectron() ? 4 : 3}>
                                        <HStack space={3} alignItems="center">
                                            <MaterialIconByName name="lock-outline" size="md" color={iconColor} />
                                            <VStack flex={1} space={0.5}>
                                                <Text fontSize="sm" fontWeight="600">
                                                    {getText('changePassword')}
                                                </Text>
                                                <Text fontSize="xs" color={secondaryTextColor}>
                                                    Update your password
                                                </Text>
                                            </VStack>
                                            <MaterialIconByName name="chevron-right" size="sm" color={secondaryTextColor} />
                                        </HStack>
                                    </Pressable>
                                </VStack>
                            </Box>

                            <Box
                                bg={cardBg}
                                borderWidth={1}
                                borderColor={borderColor}
                                borderRadius={isElectron() ? "xl" : "lg"}
                                overflow="hidden"
                                shadow={isElectron() ? "2" : "0"}
                            >
                                <Pressable
                                    onPress={() => setIsDeleteAccountModalVisible(true)}
                                    px={isElectron() ? 5 : 4}
                                    py={isElectron() ? 4 : 3}
                                >
                                    <HStack space={3} alignItems="center">
                                        <MaterialIconByName name="trash-can-outline" size="md" color="#ef4444" />
                                        <VStack flex={1} space={0.5}>
                                            <Text fontSize="sm" fontWeight="600" color="#ef4444">
                                                {getText('reqAccDeletion')}
                                            </Text>
                                            <Text fontSize="xs" color={secondaryTextColor}>
                                                Contact support to delete your account
                                            </Text>
                                        </VStack>
                                        <MaterialIconByName name="chevron-right" size="sm" color={secondaryTextColor} />
                                    </HStack>
                                </Pressable>
                            </Box>
                        </VStack>

                        {/* Preferences */}
                        <VStack space={2}>
                            <Text style={textStyle.subSectionText}>Preferences</Text>
                            <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} p={isElectron() ? 6 : 4} shadow={isElectron() ? "2" : "0"}>
                                <VStack space={4}>
                                    <VStack space={2}>
                                        <Text fontSize="sm" fontWeight="600">
                                            {getText('selectLanguage')}
                                        </Text>
                                        <LanguageDropdown />
                                    </VStack>

                                    <Divider />

                                    <HStack justifyContent="space-between" alignItems="center" space={4}>
                                        <VStack flex={1} space={0.5}>
                                            <Text fontSize="sm" fontWeight="600">
                                                {getText('darkMode')}
                                            </Text>
                                            <Text fontSize="xs" color={secondaryTextColor}>
                                                Adjust the app theme
                                            </Text>
                                        </VStack>
                                        <Switch
                                            onToggle={() => {
                                                dispatch(SettingsActions.setAppColorMode(colorMode === "dark" ? "light" : "dark"))
                                            }}
                                            isChecked={colorMode === "dark"}
                                            size="md"
                                        />
                                    </HStack>
                                </VStack>
                            </Box>
                        </VStack>

                        {/* Desktop */}
                        {showLocalProcessingSettings && canNavigateTo('DesktopSettings') && (
                            <VStack space={2}>
                                <Text style={textStyle.subSectionText}>Desktop</Text>
                                <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} overflow="hidden" shadow={isElectron() ? "2" : "0"}>
                                    <Pressable onPress={() => navigation.navigate('DesktopSettings')} px={isElectron() ? 5 : 4} py={isElectron() ? 4 : 3}>
                                        <HStack space={3} alignItems="center">
                                            <MaterialIconByName name="monitor" size="md" color={iconColor} />
                                            <VStack flex={1} space={0.5}>
                                                <Text fontSize="sm" fontWeight="600">
                                                    Processing & Updates
                                                </Text>
                                                <Text fontSize="xs" color={secondaryTextColor}>
                                                    Local processing mode and app updates
                                                </Text>
                                            </VStack>
                                            <MaterialIconByName name="chevron-right" size="sm" color={secondaryTextColor} />
                                        </HStack>
                                    </Pressable>
                                </Box>
                            </VStack>
                        )}

                        {lightboxEnabled && <SeedContourSettingsSection />}

                        {/* App */}
                        <VStack space={2}>
                            <Text style={textStyle.subSectionText}>App</Text>
                            <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} p={isElectron() ? 5 : 4} shadow={isElectron() ? "2" : "0"}>
                                <HStack space={3} alignItems="center" justifyContent="space-between">
                                    <HStack space={3} alignItems="center" flex={1}>
                                        <MaterialIconByName name="information-outline" size="md" color={iconColor} />
                                        <Text fontSize="sm" fontWeight="600">
                                            {getText('currentVersion')}
                                        </Text>
                                    </HStack>
                                    <Text fontSize="sm" color={secondaryTextColor} fontWeight="500">
                                        {currentVersion || '-'}
                                    </Text>
                                </HStack>
                            </Box>
                        </VStack>

                        {/* Grain Profiles */}
                        {canNavigateTo('GrainProfileManagement') && (
                            <VStack space={2}>
                                <Text style={textStyle.subSectionText}>{getText('grainProfilesTitle')}</Text>
                                <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius={isElectron() ? "xl" : "lg"} overflow="hidden" shadow={isElectron() ? "2" : "0"}>
                                    <Pressable onPress={() => navigation.navigate('GrainProfileManagement')} px={isElectron() ? 5 : 4} py={isElectron() ? 4 : 3}>
                                        <HStack space={3} alignItems="center">
                                            <MaterialIconByName name="barley" size="md" color={iconColor} />
                                            <VStack flex={1} space={0.5}>
                                                <Text fontSize="sm" fontWeight="600">
                                                    {getText('manageGrainProfiles')}
                                                </Text>
                                                <Text fontSize="xs" color={secondaryTextColor}>
                                                    {getText('grainProfileManagementDescription')}
                                                </Text>
                                            </VStack>
                                            <MaterialIconByName name="chevron-right" size="sm" color={secondaryTextColor} />
                                        </HStack>
                                    </Pressable>
                                </Box>
                            </VStack>
                        )}

                        {/* Diagnostics */}
                        {isNative() && (
                            <VStack space={2}>
                                <Text style={textStyle.subSectionText}>{getText('debugging')}</Text>
                                <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius="lg" p={4}>
                                    <VStack space={2}>
                                        <Text fontSize="xs" color={secondaryTextColor}>
                                            Share logs to help troubleshoot IoT connectivity issues.
                                        </Text>
                                        <Button
                                            isLoading={isUploadLogsLoading}
                                            isDisabled={rpiConnected === undefined}
                                            isLoadingText={getText('sharingLogs')}
                                            onPress={uploadIotLogs}
                                        >
                                            {getText('shareLogs')}
                                        </Button>
                                    </VStack>
                                </Box>
                            </VStack>
                        )}
                    </VStack>
                </ScrollView>
            </Box>
        </>
    );
}
